import styled from "@emotion/styled"

import Box from "@mui/material/Box"
import Slider from "@mui/material/Slider"
import Stack from "@mui/material/Stack"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { useState, useEffect } from "react"

import { NumberFont } from "../../theme"
// Placeholder for InfoOutlinedIcon
const InfoOutlinedIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
)

// TextField component
const TextField = ({
  value,
  onChange,
  error,
  InputProps,
}: {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  error?: boolean
  InputProps?: any
}) => (
  <div
    style={{
      position: "relative",
      width: "100px",
    }}
  >
    <input
      type="text"
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        height: "40px",
        padding: "8px 16px 8px 8px",
        border: `1px solid ${error ? "red" : "var(--mui-palette-borderSecondary)"}`,
        borderRadius: "8px",
        fontSize: "1rem",
        textAlign: "center",
        fontFamily: NumberFont,
      }}
    />
    {InputProps?.endAdornment && (
      <div
        style={{
          position: "absolute",
          right: "8px",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        %
      </div>
    )}
  </div>
)

// Alert component
const Alert = ({
  children,
  type = "warning",
}: {
  children: React.ReactNode
  type?: "warning" | "info"
}) => (
  <div
    style={{
      padding: "12px 16px",
      backgroundColor:
        type === "warning"
          ? "var(--mui-palette-warning-lighterBg, #fff3e0)"
          : "rgba(var(--theme-color), 0.08)",
      color:
        type === "warning" ? "var(--mui-palette-warning-dark, #bf360c)" : "rgb(var(--theme-color))",
      borderRadius: "4px",
      marginTop: "16px",
      fontSize: "0.875rem",
      border: type === "info" ? "1px solid rgba(var(--theme-color), 0.2)" : "none",
    }}
  >
    {children}
  </div>
)

interface DelegationSliderProps {
  availableCapacity: number
  totalCapacity: number
  defaultPercentage?: number
  onChange?: (percentage: number, units: number) => void
  existingBotegaFactor?: number
}

export const DelegationSlider = ({
  availableCapacity,
  totalCapacity,
  defaultPercentage = 100,
  onChange,
  existingBotegaFactor = 0,
}: DelegationSliderProps) => {
  // Convert existing Botega factor to percentage (factor is out of 10000)
  const existingBotegaPercentage = existingBotegaFactor / 100

  // Calculate max capacity percentage that can be used (available + existing Botega)
  const availableCapacityPercentage = (availableCapacity / totalCapacity) * 100

  // Calculate maximum delegation percentage (current + available)
  const maxAvailablePercentage = Math.min(
    100,
    existingBotegaPercentage + availableCapacityPercentage,
  )

  // Initialize with maximum available percentage
  const [percentage, setPercentage] = useState<number>(maxAvailablePercentage || defaultPercentage)
  const [inputValue, setInputValue] = useState<string>(
    (maxAvailablePercentage || defaultPercentage).toString(),
  )
  const [error, setError] = useState<string>("")

  // Calculate total units that will be delegated to Botega
  const totalBotegaUnits = Math.min(totalCapacity, Math.floor((percentage / 100) * totalCapacity))

  useEffect(() => {
    // Update input value when percentage changes via slider
    setInputValue(percentage.toString())

    // Call onChange callback if provided, passing the total Botega delegation
    if (onChange) {
      onChange(percentage, totalBotegaUnits)
    }
  }, [percentage, totalBotegaUnits, onChange])

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue
    setPercentage(value)
    setError("")
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setInputValue(value)

    const numValue = Number(value)
    if (!isNaN(numValue)) {
      if (numValue < 5) {
        setError("Minimum delegation is 5%")
      } else if (numValue > 100) {
        setError("Maximum delegation is 100%")
      } else {
        setError("")
        setPercentage(numValue)
      }
    } else {
      setError("Please enter a valid number")
    }
  }

  return (
    <StatsCard>
      <StatCardContent>
        <CardHeader>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Tooltip title="Delegating more capacity increases your $BOTG earnings">
              <span style={{ display: "inline-flex", opacity: 0.7 }}>
                <InfoOutlinedIcon />
              </span>
            </Tooltip>
            <Typography variant="subtitle2" color="text.secondary">
              Botega Delegation
            </Typography>
          </div>
          <TextField
            value={inputValue}
            onChange={handleInputChange}
            error={!!error}
            InputProps={{
              endAdornment: true,
            }}
          />
        </CardHeader>

        <Box>
          <Slider
            value={percentage}
            onChange={handleSliderChange}
            min={5}
            max={100}
            step={5}
            color="accent"
            valueLabelDisplay="auto"
            aria-label="Delegation percentage"
            sx={{
              "& .MuiSlider-markLabel": {
                fontSize: "0.75rem",
                fontWeight: "bold",
                marginTop: "5px",
                color: "text.secondary",
              },
            }}
          />
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="column">
              <Typography variant="caption" color="text.secondary">
                Current Botega Delegation
              </Typography>
              <DelegationValue>{existingBotegaPercentage.toFixed(0)}%</DelegationValue>
            </Stack>
            <Stack direction="column" alignItems="flex-end">
              <Typography variant="caption" color="text.secondary">
                New Delegation
              </Typography>
              <DelegationValue className="accent">{percentage.toFixed(0)}%</DelegationValue>
            </Stack>
          </Stack>
        </Box>

        {error && <Alert>{error}</Alert>}

        <EarningsInfo>
          <InfoIcon>💡</InfoIcon>
          <span>Set to 100% to maximize your $BOTG earnings</span>
        </EarningsInfo>

        {/*         {showRedistributionMessage && (
          <EarningsInfo className="warning">
            <InfoIcon>⚠️</InfoIcon>
            <span>
              This will redistribute {unitsFromOtherDelegations} units from other delegations
            </span>
          </EarningsInfo>
        )} */}
      </StatCardContent>
    </StatsCard>
  )
}

// Styled components matching the StatsCard style from DelegationCapacity
const StatsCard = styled.div`
  padding: 1.25rem;
  height: 100%;
  border: 1px solid var(--mui-palette-divider);
  border-radius: var(--mui-shape-borderRadius);
  transition: all 0.2s ease;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

  [data-mui-color-scheme="dark"] & {
    background-color: var(--mui-palette-background-paper);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`

const StatCardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 1rem;
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DelegationValue = styled.div`
  font-family: ${NumberFont};
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--mui-palette-text-primary);

  &.accent {
    color: rgb(var(--theme-color));
  }
`

const EarningsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: rgba(var(--theme-color), 0.08);
  color: rgb(var(--theme-color));
  margin: 0.25rem 0;

  &.success {
    background-color: rgba(var(--mui-palette-success-main-rgb), 0.1);
    color: var(--mui-palette-success-main);
  }

  &.warning {
    background-color: rgba(var(--mui-palette-warning-main-rgb), 0.1);
    color: var(--mui-palette-warning-main);
  }

  span {
    line-height: 1.2;
  }
`

const InfoIcon = styled.span`
  font-size: 1rem;
`

export default DelegationSlider
